import { useRef, useState } from 'react'
import './notification-inside.scss'
// library
import Slider from 'react-slick'
// hooks
import useUIActions from '../../../hooks/useUIActions'
import useNotification from '../../../hooks/useNotification'
// content
import { useContentfulNotification } from '../../../hooks/contentful'
// utils
import sendGaPush from '../../../utils/sendGaPush'
// components
import Card from '../../atoms/card/card'
import CardNotification from '../cards/card-notification/card-notification'
import Button from '../../atoms/button/button'
import Loading from '../../atoms/loading/loading'
import Image from '../../atoms/Image/image'
import CheckInput from '../../atoms/check-input/check-input'
import BlankPage from '../../atoms/blank-page/blank-page'
// icons
import { ArrowLeftIcon, GearIcon } from '../../atoms/icons'

const NotificationInside = () => {
	// content
	const { referenciaContenidoCollection, loading, error } = useContentfulNotification()

	// Procesar datos para mantener compatibilidad con la estructura original
	const notificationData = {
		notifications: {
			title: referenciaContenidoCollection?.[0]?.tituloGeneral || '',
			description: referenciaContenidoCollection?.[0]?.tituloDescripcion || '',
			button: referenciaContenidoCollection?.[0]?.tituloBoton || ''
		},
		noNotifications: {
			img: referenciaContenidoCollection?.[1]?.imagenPrincipal || '',
			title: referenciaContenidoCollection?.[1]?.tituloGeneral || ''
		},
		configuration: {
			title: referenciaContenidoCollection?.[2]?.tituloGeneral || '',
			content: referenciaContenidoCollection?.[2]?.referenciaContenidoCollection?.items || false
		},
		modal: {
			img: referenciaContenidoCollection?.[3]?.imagenPrincipal || '',
			title: referenciaContenidoCollection?.[3]?.tituloGeneral || '',
			description: referenciaContenidoCollection?.[3]?.tituloDescripcion || '',
			buttons: referenciaContenidoCollection?.[3]?.referenciaContenidoCollection?.items
		}
	}

	const { notifications, noNotifications, configuration, modal } = notificationData

	// data
	const [limitShowNotification, setLimitShowNotification] = useState(10)

	// hook
	const { hideOverlay } = useUIActions()
	const {
		config,
		notifications: notificationsBack,
		setModal,
		setPopup,
		isStep,
		loading: backLoading,
		openModalConfiguration,
		handledStep,
		setDay,
		goToNotification
	} = useNotification({ modalContent: modal, geaAction: 'ClicNotificacion' })

	// ref
	const refSlider = useRef(null)

	// metrics
	const handleSendGaPush = ({ action = 'ClicNotificacion', label }) => {
		sendGaPush({ category: 'SaludContinuarediseno', action, label })
	}

	// actions
	const showAllNotifications = () => setLimitShowNotification(notificationsBack.length)

	// render
	const notificationsRender = (
		<div className='m-notification-inside__cont'>
			{notificationsBack && notificationsBack.length > 0 ? (
				<>
					{notifications.title && <h3>{notifications.title}</h3>}
					{notifications.description && <p>{notifications.description}</p>}

					{notificationsBack.slice(0, limitShowNotification).map((notify, index) => (
						<CardNotification
							key={notify.notificationId + index + notify.generationDate}
							title={notify.title}
							time={setDay(notify.generationDate)}
							description={notify.description}
							active={!notify.seen}
							onClick={() => {
								handleSendGaPush({ label: `Notificación - ${notify.title}` })
								goToNotification({ url: notify.url, id: notify.notificationId })
							}}
						/>
					))}

					{notifications.button && notificationsBack.length > limitShowNotification && (
						<Button
							title={notifications.button}
							uppercase
							classes='flat underline mt2'
							onClick={() => {
								handleSendGaPush({ label: notifications.button })
								showAllNotifications()
							}}
						/>
					)}
				</>
			) : (
				<>
					<Image
						src={noNotifications.img.url}
						alt={noNotifications.img.title}
						width='66'
						height='66'
						classes='ml mr mt3'
					/>
					{noNotifications.title && (
						<h2 className='ml mr' style={{ maxWidth: '160px' }}>
							{noNotifications.title}
						</h2>
					)}
				</>
			)}
		</div>
	)
	const groupByCategory = cat => {
		const allCategories = []
		cat &&
			cat?.forEach((obj, i) => {
				const category = obj.category
				const index = allCategories?.findIndex(cat => cat?.category === category)
				if (index === -1) {
					allCategories.push({
						category,
						id: i,
						items: [obj]
					})
				} else {
					allCategories[index].items.push(obj)
				}
			})
		return allCategories
	}
	const transformedData = groupByCategory(config)
	const configurationRender = (
		<div className='m-notification-inside__cont'>
			{configuration.title && <h3>{configuration.title}</h3>}
			<Card>
				{transformedData?.map(data => {
					return (
						<div key={data.id}>
							<p className='m-notification-inside__cont__title'>{data.category}</p>
							{data.items.map(item => {
								return (
									<div
										className='m-notification-inside__cont__elements'
										key={item.userConfigurationId}
									>
										<header>
											<p className='m-notification-inside__cont__subtitle'>
												{item.configurationName}
											</p>
											<CheckInput
												rounded
												active={item.active}
												onClick={() => {
													handleSendGaPush({ label: `Configuración - ${item.configurationName}` })
													openModalConfiguration({
														id: item.userConfigurationId,
														currentActivated: item.active
													})
												}}
											/>
										</header>
										<p className='m-notification-inside__cont__description'>
											{item.configurationName}
										</p>
									</div>
								)
							})}
						</div>
					)
				})}
			</Card>
		</div>
	)

	const renderNotification = [notificationsRender, configurationRender]

	// actions
	const showConfiguration = () => {
		if (!refSlider || isStep === 1) return null

		refSlider.current.slickNext()
		return handledStep(1)
	}

	const showListNotifications = () => {
		if (!refSlider) return null

		if (isStep !== 0) {
			refSlider.current.slickPrev()
			return handledStep(0)
		}

		return hideOverlay()
	}

	// slider settings
	const sliderSettings = {
		arrows: false,
		dots: false,
		lazyLoad: true,
		infinite: false,
		slidesToShow: 1,
		swipeToSlide: false,
		swipe: false,
		className: 'm-notification-inside__slide',
		adaptiveHeight: true
	}

	if (loading || backLoading) {
		return (
			<div className='container'>
				{setPopup}
				<Loading small />
			</div>
		)
	}

	return (
		<section className='m-notification-inside'>
			{setModal}
			{setPopup}

			<button
				className='m-notification-inside__btn_config'
				onClick={() => showListNotifications()}
				style={{ left: '2rem' }}
			>
				<ArrowLeftIcon color='#fff' />
			</button>

			{!error && (
				<button
					className='m-notification-inside__btn_config'
					onClick={() => showConfiguration()}
					style={{ right: '2rem' }}
				>
					<GearIcon color='#fff' />
				</button>
			)}

			{error ? (
				<BlankPage />
			) : (
				<Slider {...sliderSettings} ref={refSlider}>
					{renderNotification}
				</Slider>
			)}
		</section>
	)
}

export default NotificationInside
